import { Box, Typography, styled } from "@mui/material";
import React from "react";

export const LegendDirection = {
    ROW: 'row',
    COLUMN: 'column',
};

export const LegendShape = {
    SQUARE: 'square',
    CIRCLE: 'circle',
};

interface LegendItem {
    color: string;
    dashed?: boolean;
    label: string;
}

interface ChartLegendProps {
    items: LegendItem[];
    direction?: typeof LegendDirection[keyof typeof LegendDirection];
    shape?: typeof LegendShape[keyof typeof LegendShape];
    small?: boolean;
}

const Root = styled(Box)<{ $direction: string }>(({ theme, $direction }) => ({
    alignItems: $direction === 'column' ? 'flex-start' : 'center',
    display: 'flex',
    flexDirection: $direction as 'row' | 'column',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    justifyContent: 'center',
    lineHeight: 'inherit',
}));

const ItemRow = styled(Box)({
    alignItems: 'center',
    display: 'flex',
});

const DashedIconWrapper = styled(Box)<{ $small: boolean }>(({ theme, $small }) => ({
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    height: $small ? 12 : 18,
    marginRight: $small ? theme.spacing(0.5) : theme.spacing(1),
    width: $small ? 12 : 24,
}));

const DashedLine = styled(Box)<{ $color: string }>(({ $color }) => ({
    borderTop: `3px dashed ${$color}`,
    width: '100%',
}));

const ColorSwatch = styled(Box)<{ $circle: boolean; $color: string; $small: boolean }>(({ theme, $circle, $color, $small }) => ({
    backgroundColor: $color,
    borderRadius: $circle ? '50%' : 2,
    flexShrink: 0,
    height: $small ? 12 : 18,
    marginRight: $small ? theme.spacing(0.5) : theme.spacing(1),
    width: $small ? 12 : 18,
}));

const ChartLegend: React.FC<ChartLegendProps> = ({ items, direction = LegendDirection.ROW, shape = LegendShape.SQUARE, small = false }) => {
    const isCircle = shape === LegendShape.CIRCLE;

    return (
        <Root $direction={direction}>
            {items.map((item, index) => (
                <ItemRow key={index}>
                    {item.dashed ? (
                        <DashedIconWrapper $small={small}>
                            <DashedLine $color={item.color} />
                        </DashedIconWrapper>
                    ) : (
                        <ColorSwatch $circle={isCircle} $color={item.color} $small={small} />
                    )}
                    <Typography variant={small ? 'caption' : 'body2'} lineHeight="inherit">
                        {item.label}
                    </Typography>
                </ItemRow>
            ))}
        </Root>
    );
};

export default ChartLegend;